import React, { useState } from 'react';
import { Avatar, confirmAlert } from 'components/common';
import PropTypes from 'prop-types';
import InteractionTime from './InteractionTime';
import { FaClock, FaComments } from 'react-icons/fa';
import {
  MdOutlineRefresh,
  MdOutlineEdit,
  MdOutlineCheckCircle,
  MdOutlineDelete,
  MdOutlineError,
  MdOutlineWarningAmber,
  MdOutlineInfo,
} from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  closeTopic, deleteTopic, updateTopic,
  getTopicResponses, onToggleResponses,
} from 'redux/crm/project/interactionSlice';
import TopicForm from './TopicForm';

// ── Config maps ───────────────────────────────────────────────────
const PRIORITY_MAP = {
  high:   { label: 'High',   bg: '#fef2f2', color: '#dc2626', border: '#fca5a5', Icon: MdOutlineError },
  medium: { label: 'Medium', bg: '#fffbeb', color: '#d97706', border: '#fcd34d', Icon: MdOutlineWarningAmber },
  low:    { label: 'Low',    bg: '#eff6ff', color: '#2563eb', border: '#93c5fd', Icon: MdOutlineInfo },
};

const STATUS_MAP = {
  open:   { label: 'Open',   bg: '#ecfdf5', color: '#059669', border: '#6ee7b7' },
  closed: { label: 'Closed', bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
};

// ── Helper: icon action button ────────────────────────────────────
const ActionBtn = ({ onClick, title, children, accentColor }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 34, height: 34, borderRadius: 8, border: 'none',
      background: 'transparent', color: accentColor || '#6b7280',
      cursor: 'pointer', transition: 'background 0.15s, transform 0.1s',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = `${accentColor || '#6b7280'}18`; e.currentTarget.style.transform = 'scale(1.1)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.transform = 'scale(1)'; }}
  >
    {children}
  </button>
);
ActionBtn.propTypes = {
  onClick: PropTypes.func.isRequired,
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  accentColor: PropTypes.string,
};

// ── Pill badge ────────────────────────────────────────────────────
const Pill = ({ bg, color, border, children }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 10px', borderRadius: 20,
    background: bg, color, border: `1px solid ${border}`,
    fontSize: 11, fontWeight: 600, lineHeight: 1,
  }}>
    {children}
  </span>
);
Pill.propTypes = {
  bg: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  border: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

// ── Main component ────────────────────────────────────────────────
const TopicHeader = ({ topic }) => {
  const { responses = [] } = useSelector((s) => s.crm_project_interaction || {});
  const [showEditForm, setShowEditForm] = useState(false);
  const dispatch = useDispatch();

  const projectId = topic.project?.id;
  const topicId   = topic?.id;
  // allow_delete is decided server-side: unanswered, still open, raised by this
  // user (or an admin).
  const { allow_edit, allow_delete, allow_close } = topic.permissions || {};

  const priority = PRIORITY_MAP[topic.priority] || PRIORITY_MAP.medium;
  const status   = STATUS_MAP[topic.status]   || STATUS_MAP.open;

  const handleRefresh = () => {
    const current = responses[topicId];
    dispatch(onToggleResponses(topicId));
    const isOpening = !current?.visible;
    const notLoaded = !current?.loaded;
    if (isOpening && notLoaded) {
      dispatch(getTopicResponses({ projectId, topicId, params: new URLSearchParams({ page: '1', per_page: '5' }).toString() }));
    }
  };

  const handleClose = () => {
    confirmAlert({
      title: 'Confirmation',
      message: `Are you sure to <b>close</b> this topic?`,
      closeOnEscape: true,
      closeOnClickOutside: false,
      buttons: [
        {
          label: 'Yes',
          onClick: async () => {
            try { await dispatch(closeTopic({ projectId, topicId })).unwrap(); }
            catch { toast.error('Failed to close topic.'); }
          },
        },
        { label: 'No', onClick: () => {} },
      ],
    });
  };


  const handleDelete = () => {
    confirmAlert({
      title: 'Confirmation',
      message: `Are you sure to delete topic <strong class='tw-text-blue-600'>${topic.name}</strong>?`,
      closeOnEscape: true,
      closeOnClickOutside: false,
      buttons: [
        {
          label: 'Yes',
          onClick: async () => {
            try { await dispatch(deleteTopic({ projectId, topicId })).unwrap(); }
            catch { toast.error('Failed to delete topic.'); }
          },
        },
        { label: 'No', onClick: () => {} },
      ],
    });
  };

  const handleSubmitEdit = async (data) => {
    const processedData = {
      ...data,
      tags: Array.isArray(data.tags) ? data.tags.map((t) => t.value) : [],
      recipient_names: Array.isArray(data.recipient_names) ? data.recipient_names.map((r) => r.value) : [],
    };
    try {
      await dispatch(updateTopic({ projectId, topicId, data: processedData })).unwrap();
      setShowEditForm(false);
    } catch {
      toast.error('Failed to update topic. Please try again.');
    }
  };

  return (
    <>
      <div style={{ padding: '18px 20px 14px', background: '#fff' }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>

          {/* Author avatar */}
          <div style={{ flexShrink: 0, paddingTop: 3 }}>
            <Avatar src={topic.author?.avatar} name={topic.author?.name} size="3xl" />
          </div>

          {/* Main area */}
          <div style={{ flex: 1, minWidth: 0 }}>

            {/* Title + action buttons row */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
              <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.35, wordBreak: 'break-word' }}>
                {topic.name}
              </h4>
              <div style={{ display: 'flex', gap: 2, flexShrink: 0, marginTop: -3 }}>
                <ActionBtn onClick={handleRefresh} title="Reload responses" accentColor="#6366f1">
                  <MdOutlineRefresh size={20} />
                </ActionBtn>
                {allow_edit && (
                  <ActionBtn onClick={() => setShowEditForm(true)} title="Edit topic" accentColor="#3b82f6">
                    <MdOutlineEdit size={19} />
                  </ActionBtn>
                )}
                {allow_close && topic.status !== 'closed' && (
                  <ActionBtn onClick={handleClose} title="Resolve / close topic" accentColor="#10b981">
                    <MdOutlineCheckCircle size={20} />
                  </ActionBtn>
                )}
                {allow_delete && (
                  <ActionBtn onClick={handleDelete} title="Delete topic" accentColor="#ef4444">
                    <MdOutlineDelete size={20} />
                  </ActionBtn>
                )}
              </div>
            </div>

            {/* Badges row */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              <Pill bg={priority.bg} color={priority.color} border={priority.border}>
                <priority.Icon size={10} />
                {priority.label}
              </Pill>
              <Pill bg={status.bg} color={status.color} border={status.border}>
                {status.label}
              </Pill>
            </div>

            {/* Meta row: time + reply count */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 12, color: '#64748b', marginBottom: 8 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <FaClock size={10} />
                <InteractionTime date={topic.created_at} />
              </span>
              <span style={{ color: '#94a3b8' }}>·</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#3b82f6', fontWeight: 500 }}>
                <FaComments size={10} />
                {topic.response_count} {topic.response_count === 1 ? 'reply' : 'replies'}
              </span>
            </div>

            {/* Tags */}
            {topic.tags?.length > 0 && (
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                {topic.tags.map((tag, i) => (
                  <span key={i} style={{
                    padding: '2px 9px', borderRadius: 20,
                    background: '#eff6ff', color: '#3b82f6',
                    border: '1px solid #bfdbfe',
                    fontSize: 11, fontWeight: 500,
                  }}>
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showEditForm && (
        <TopicForm
          show={showEditForm}
          onHide={() => setShowEditForm(false)}
          onSubmit={handleSubmitEdit}
          record={topic}
        />
      )}
    </>
  );
};

TopicHeader.propTypes = {
  topic: PropTypes.object.isRequired,
};

export default TopicHeader;
